import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { 
  Play, 
  Pause, 
  Square, 
  Mic, 
  MicOff, 
  Video, 
  VideoOff,
  Users,
  Clock,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { UserProfile } from "@shared/types";
import { formatDate } from "@/lib/utils";

interface LiveMeetingProps {
  user: UserProfile;
}

interface MeetingData {
  id: string;
  title: string;
  type: string;
  date: string;
  location: string;
  locationId: string;
  templateId: string;
  attendees: string[];
  template?: {
    id: string;
    name: string;
    items: AgendaItem[];
  };
}

interface AgendaItem {
  id?: string;
  title: string;
  content: string;
  duration: number;
  type?: string;
  notes?: string;
  startTime?: string;
  endTime?: string;
  status?: 'pending' | 'active' | 'completed';
}

export default function LiveMeeting({ user }: LiveMeetingProps) {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const navigate = (path: string) => setLocation(path);
  const { toast } = useToast();
  
  // State management
  const [currentAgendaIndex, setCurrentAgendaIndex] = useState(0);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [meetingStartTime, setMeetingStartTime] = useState<Date | null>(null);
  const [meetingElapsedTime, setMeetingElapsedTime] = useState(0);
  const [itemElapsedTime, setItemElapsedTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  
  // Fetch meeting data
  const { data: meeting, isLoading } = useQuery<MeetingData>({
    queryKey: ['meeting', id],
    queryFn: async () => {
      console.log('🎯 Fetching meeting data for ID:', id);
      const response = await fetch(`/api/meetings/${id}`, { redirect: 'error' });
      if (!response.ok) throw new Error('Failed to fetch meeting');
      const meetingData = await response.json();
      
      // Fetch template if available
      if (meetingData.templateId) {
        const templateResponse = await fetch(`/api/templates/${meetingData.templateId}`, { redirect: 'error' });
        if (templateResponse.ok) {
          const template = await templateResponse.json();
          meetingData.template = template;
        }
      }
      
      return meetingData;
    },
    enabled: !!id
  });
  
  // Initialize agenda items when meeting data loads
  useEffect(() => {
    if (meeting?.template?.items) {
      const initializedItems = meeting.template.items.map((item, index) => ({
        ...item,
        id: `item-${index}`,
        status: 'pending' as const,
        notes: ''
      }));
      setAgendaItems(initializedItems);
    }
  }, [meeting]);
  
  // Start meeting
  const startMeeting = () => {
    console.log('🚀 Starting meeting');
    const now = new Date();
    setMeetingStartTime(now);
    setIsRecording(true);
    
    // Start the first agenda item
    if (agendaItems.length > 0) {
      const updatedItems = [...agendaItems];
      updatedItems[0] = {
        ...updatedItems[0],
        status: 'active',
        startTime: now.toISOString()
      };
      setAgendaItems(updatedItems);
    }
    
    toast({
      title: "Meeting Started",
      description: "Meeting is now in progress"
    });
  };
  
  // Move to next agenda item
  const moveToNextItem = () => {
    if (currentAgendaIndex < agendaItems.length - 1) {
      // Complete current item
      const updatedItems = [...agendaItems];
      updatedItems[currentAgendaIndex] = {
        ...updatedItems[currentAgendaIndex],
        status: 'completed',
        endTime: new Date().toISOString()
      };
      
      // Start next item
      const nextIndex = currentAgendaIndex + 1;
      updatedItems[nextIndex] = {
        ...updatedItems[nextIndex],
        status: 'active',
        startTime: new Date().toISOString()
      };
      
      setAgendaItems(updatedItems);
      setCurrentAgendaIndex(nextIndex);
      setItemElapsedTime(0);
    }
  };
  
  // End meeting
  const endMeeting = async () => {
    console.log('🏁 Ending meeting');
    setIsRecording(false);
    
    toast({
      title: "Meeting Ended",
      description: "Meeting has been completed"
    });
    
    // Navigate back to meetings
    navigate('/meeting-agendas');
  };
  
  // Format time display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading meeting...</p>
        </div>
      </div>
    );
  }
  
  if (!meeting) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Meeting Not Found</h2>
          <p className="text-muted-foreground mb-4">The meeting you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/meeting-agendas')}>
            Back to Meetings
          </Button>
        </Card>
      </div>
    );
  }
  
  const currentItem = agendaItems[currentAgendaIndex];
  
  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Meeting Header */}
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{meeting.title}</CardTitle>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDate(meeting.date)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {meeting.attendees.length} attendees
                </span>
                <Badge variant={meetingStartTime ? "default" : "secondary"}>
                  {meetingStartTime ? "In Progress" : "Not Started"}
                </Badge>
                {isRecording && (
                  <Badge variant="destructive" className="animate-pulse">
                    🔴 Recording
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Meeting Controls */}
            <div className="flex items-center gap-2">
              {!meetingStartTime ? (
                <Button onClick={startMeeting} size="lg">
                  <Play className="mr-2 h-4 w-4" />
                  Start Meeting
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setIsMuted(!isMuted)}
                    title={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setIsVideoOn(!isVideoOn)}
                    title={isVideoOn ? "Turn off video" : "Turn on video"}
                  >
                    {isVideoOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={endMeeting}
                  >
                    <Square className="mr-2 h-4 w-4" />
                    End Meeting
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Meeting Content */}
      {meetingStartTime && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Agenda Item */}
            {currentItem && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      {currentItem.title}
                    </CardTitle>
                    <Badge>{currentItem.duration} min</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{currentItem.content}</p>
                  
                  {/* Notes Section */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Meeting Notes</label>
                    <Textarea
                      placeholder="Add notes for this agenda item..."
                      value={currentItem.notes || ''}
                      onChange={(e) => {
                        const updatedItems = [...agendaItems];
                        updatedItems[currentAgendaIndex] = {
                          ...updatedItems[currentAgendaIndex],
                          notes: e.target.value
                        };
                        setAgendaItems(updatedItems);
                      }}
                      className="min-h-[100px]"
                    />
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex justify-between mt-6">
                    <div className="text-sm text-muted-foreground">
                      Item {currentAgendaIndex + 1} of {agendaItems.length}
                    </div>
                    <Button 
                      onClick={moveToNextItem}
                      disabled={currentAgendaIndex >= agendaItems.length - 1}
                    >
                      Next Item
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Meeting Progress */}
            <Card>
              <CardHeader>
                <CardTitle>Meeting Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Overall Progress</span>
                    <span>{Math.round((currentAgendaIndex / agendaItems.length) * 100)}%</span>
                  </div>
                  <Progress value={(currentAgendaIndex / agendaItems.length) * 100} />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Time Elapsed</span>
                    <span>{formatTime(meetingElapsedTime)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Agenda Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Agenda Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {agendaItems.map((item, index) => (
                    <div 
                      key={index}
                      className={`flex items-center gap-2 p-2 rounded ${
                        index === currentAgendaIndex ? 'bg-primary/10' : ''
                      }`}
                    >
                      {item.status === 'completed' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : item.status === 'active' ? (
                        <div className="h-4 w-4 rounded-full bg-primary animate-pulse" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted" />
                      )}
                      <span className="text-sm flex-1">{item.title}</span>
                      <span className="text-xs text-muted-foreground">{item.duration}m</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
} 